import { ALL_CENTRES } from '@appTypes/auth';
import { useAppShellStore } from '@store/appShellStore';
import { useAuthStore } from '@store/authStore';

/**
 * Shared session / main-centre / centre scope logic for both the desktop topbar
 * chips (ScopeSwitcher) and the mobile nav drawer. Centralises the store wiring
 * and the side effects on change: all three persist the new value and then
 * hard-reload, so the app re-bootstraps on the new scope. Every scoped query,
 * cache and socket room is rebuilt from scratch, which is the only way to be
 * sure nothing from the previous scope survives — see changeSession for what
 * went wrong when this was done in place.
 *
 * "All centres" is a read-only rollup across every centre of the selected main
 * centre — available to admins only, and refused by every write route.
 */
export const useScopeSwitcher = () => {
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const {
    mainCentres, centres, sessions,
    mainCentreId, centreId, sessionId,
    setMainCentreId, setCentreId, setSessionId,
  } = useAppShellStore();

  const isRollup = centreId === ALL_CENTRES;
  const activeMainCentres = mainCentres.filter((m) => m.active);
  const activeCentres = centres.filter((c) => c.active);

  const currentMainCentreName = mainCentres.find((m) => m._id === mainCentreId)?.name ?? 'Main Centre';
  const currentCentreName = isRollup
    ? 'All Centres'
    : centres.find((c) => c._id === centreId)?.name ?? 'Location';
  const currentSessionLabel = sessions.find((s) => s._id === sessionId)?.label ?? 'Session';

  // Switching city invalidates the current centre, so clear it — the bootstrap
  // then settles on the first active centre of the new main centre.
  const changeMainCentre = (id: string) => {
    if (id === mainCentreId) return;
    setMainCentreId(id);
    setCentreId('');
    window.location.reload();
  };

  const changeCentre = (id: string) => {
    if (id === centreId) return;
    setCentreId(id);
    window.location.reload();
  };

  // Reloads, exactly like the two centre switches above. Clearing the query
  // cache in place looked lighter but didn't actually work: only the dashboard
  // key carries the scope, so every other query (admissions, installments,
  // transactions, users, reports) kept its cached entry under an identical key,
  // and queryClient.clear() does not force mounted observers to refetch — the
  // page went on showing the previous session's records. Scoping every key by
  // hand would fix it too, but silently regresses the moment a new query is
  // added without the scope; a reload re-bootstraps the whole app and cannot be
  // partially right.
  const changeSession = (id: string) => {
    if (id === sessionId) return;
    setSessionId(id);
    window.location.reload();
  };

  return {
    isAdmin,
    isRollup,
    sessions,
    activeMainCentres,
    activeCentres,
    mainCentreId,
    centreId,
    sessionId,
    currentMainCentreName,
    currentCentreName,
    currentSessionLabel,
    changeMainCentre,
    changeCentre,
    changeSession,
  };
};
